import { usePageContext } from "lib/context/page-context";
import { ContentBlocks } from "blocks/types";
import Blocks from "../Blocks";
import { BlockDropzone } from "components/Blocks/BlockDropzone";
import { LoggedInConsumerLabel } from "components/LoggedInConsumerLabel";
import { trpc } from "utils/trpc";
import { useRouter } from "next/router";
import { Button } from "@zapier/design-system";
import { projectPath } from "lib/route-helpers";
import { ResponsiveNav } from "blocks/components/ResponsiveNav";
import { useMemo } from "react";
import { withHttps } from "utils/withHttps";
import { cn } from "utils/cn";
import { useEmbed } from "lib/context/embed-context";

export const PageWrapper: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $isEditing: boolean;
  }
> = ({ $isEditing, ...props }) => {
  const { noBackground } = useEmbed();
  return (
    <div
      {...props}
      className={cn(
        "text-zi-text",
        {
          "min-h-full p-[50px_15px]": $isEditing,
          "min-h-screen p-[50px_15px_80px]": !$isEditing,
          "bg-zi-pageBackground": !noBackground,
          "bg-transparent": noBackground,
        },
        props.className
      )}
    />
  );
};

type ConsumerInfo =
  | { name: string | null; email: string; avatar: string; id: string }
  | undefined;

export function Page(props: {
  blocks: ContentBlocks;
  consumerInfo?: ConsumerInfo;
}) {
  const {
    allPages,
    globalNavigation,
    includeLogo,
    isEditing,
    isThumbnail,
    navigationLinks,
    navigationEnabled,
    pageId,
    projectId,
    projectName,
    logo,
  } = usePageContext();

  const utils = trpc.useContext();
  const router = useRouter();

  const { logoHref, navItems } = useMemo(() => {
    const hasAllPages = typeof allPages === "object" && allPages !== null;
    const canBuildLinkedPages = Array.isArray(navigationLinks) && hasAllPages;

    const homepage = hasAllPages
      ? Object.values(allPages).find((p) => p.isHomepage)
      : undefined;

    const homepageLogoHref = homepage ? homepage.url : "/";

    return canBuildLinkedPages
      ? {
          logoHref: homepageLogoHref,
          navItems: navigationLinks.map((navLink) => {
            if (navLink.type === "page") {
              const page = allPages[navLink.pageId];
              return {
                current: page.id === pageId,
                href: page.url,
                name: page.title,
              };
            }
            return {
              current: false,
              href: withHttps(navLink.url),
              name: navLink.title,
              target: "_blank" as const,
            };
          }),
        }
      : {
          logoHref: homepageLogoHref,
          navItems: [],
        };
  }, [allPages, navigationLinks, pageId]);

  const profileItems = useMemo(() => {
    if (!props.consumerInfo) return [];

    return [
      {
        name: "Log out",
        onClick() {
          utils.projectAuth.logout
            .fetch()
            .catch((error) => {
              console.error("Failed to logout:", error);
            })
            .finally(() => router.replace("/"));
        },
      },
    ];
  }, [props.consumerInfo, router, utils.projectAuth.logout]);

  return (
    <>
      {globalNavigation && navigationEnabled && (
        <div className="sticky top-0 z-[1]">
          <div
            data-editing={isEditing}
            className={cn(
              "group relative [&[data-editing='true']:hover>:first-child]:blur-[2px] [&[data-editing='true']>:first-child]:blur-0 [&[data-editing='true']>:first-child]:transition-all [&[data-editing='true']>:first-child]:duration-200"
            )}
          >
            {!isThumbnail && (
              <ResponsiveNav
                interfaceName={projectName}
                items={navItems}
                profileItems={profileItems}
                name={props.consumerInfo?.name}
                email={props.consumerInfo?.email}
                avatarUrl={props.consumerInfo?.avatar}
                avatarAltText={props.consumerInfo?.name}
                includeLogo={!!logo && Boolean(includeLogo)}
                logoHref={logoHref}
                logoAltText={projectName}
                logo={logo}
              />
            )}
            {isEditing && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="pointer-events-none -translate-y-5 opacity-0 transition-all duration-150 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
                  <Button
                    color="primary-alt"
                    iconBefore="actionEdit"
                    href={
                      projectPath({ projectId: projectId as string }) +
                      "/settings?tab=navigation"
                    }
                    size="xsmall"
                    type="button"
                  >
                    Edit in settings
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <PageWrapper $isEditing={isEditing}>
        <BlockDropzone
          className={cn("flex flex-col flex-nowrap items-center gap-y-10", {
            "min-h-screen": isEditing,
          })}
          disableDrop={!isEditing}
        >
          <Blocks blocks={props.blocks} />
        </BlockDropzone>
        {props.consumerInfo && !isEditing && (
          <LoggedInConsumerLabel consumer={props.consumerInfo} />
        )}
      </PageWrapper>
    </>
  );
}
